import { PageRequest } from '@aiz-gl/omni-ui';
import {
  Address,
  BillingAddressResponse,
  OrderDetailResponse,
  OrderHistorySummary,
  OrderHistorySummaryResponse,
  OrderItem,
  OrderItemResponse,
  OrderSearch,
  OrderSearchParams,
  ShippingAddressResponse,
} from '../../models/order.models';

export function getOrderNumberFromResponse(
  response: OrderDetailResponse | OrderHistorySummaryResponse,
) {
  return `${response.web_order_id}-${response.shipment_id}`;
}

export function getEncodedDate(date?: Date | string) {
  if (typeof date === 'string') return date;
  return date ? encodeURIComponent(date.toISOString()) : '';
}

export function mapOrderHistorySummaryResponseToModel(
  response: OrderHistorySummaryResponse,
): OrderHistorySummary {
  const source = response as unknown as { [key: string]: unknown };
  const rmaCountValue = source['rma_count'] ?? source['rmaCount'];

  return {
    orderId: response.atb_id,
    orderNumber: getOrderNumberFromResponse(response),
    webOrderId: response.web_order_id,
    erpOrderNumber: response.erp_order_number || '',
    salesChannelCode: response.sales_channel_code || '',
    orderTotal: response.order_total,
    buyerCompanyName: response.buyer_company_name,
    orderDate: new Date(response.order_date),
    currencyCode: response.pay_currency_code || response.currency_code,
    warehouseCode: response.warehouse_code,
    parentWarehouseCode: response.parent_warehouse_code || undefined,
    dccCode: response.dcc_code,
    shipmentId: response.shipment_id,
    status: response.status,
    totalQty: response.total_qty,
    rmaCount: typeof rmaCountValue === 'number' ? rmaCountValue : undefined,
  };
}

export function mapAddressResponseToModel(
  response: BillingAddressResponse | ShippingAddressResponse,
): Address {
  return {
    firstName: response.first_name,
    lastName: response.last_name,
    middleName: response.middle_name,
    address1: response.address_1 || response.address_line_1 || '',
    address2: response.address_2 || response.address_line_2 || '',
    city: response.city,
    state: response.state,
    county: response.county,
    country: response.country,
    postalCode: response.postal_code,
    createdOn: new Date(response.created_on),
    updatedOn: new Date(response.updated_on),
  };
}

export function mapOrderItemResponseToModel(
  response: OrderItemResponse,
): OrderItem {
  return {
    productId: response.product_id,
    sku: response.variant_id,
    productName: response.product_name,
    quantity: response.qty,
    unitPrice: response.unit_price,
    totalPrice: response.total_price,
    currencyCode: response.currency_code,
    createdOn: new Date(response.created_on),
    updatedOn: new Date(response.updated_on),
  };
}

export function mapOrderPageRequestToParams(
  search: PageRequest,
): OrderSearchParams {
  const {
    regionId,
    buyerCompanyName,
    orderNumber,
    erpOrderNumber,
    salesChannelCode,
    startDate,
    endDate,
    dcc,
    warehouse,
    wms,
    status,
    includeRmaCount,
  } = search.filter as OrderSearch;
  return {
    pageNumber: search.pageNumber,
    pageSize: search.pageSize,
    sortBy: search.sortColumn,
    sortDirection: search.sortDirection,
    regionId: regionId,
    buyerCompanyName: buyerCompanyName,
    orderNo: orderNumber,
    erpOrderNumber: erpOrderNumber,
    salesChannelCode: salesChannelCode,
    startDate: getEncodedDate(startDate),
    endDate: getEncodedDate(endDate),
    dcc: dcc,
    warehouse: warehouse,
    erp: wms,
    status: status,
    includeRmaCount: includeRmaCount,
  };
}
